import { useCallback, useMemo } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@clerk/react";
import {
  Trash,
  ArrowRight,
  ArrowLeft,
  PaperPlaneTilt,
  Power,
  Prohibit,
} from "@phosphor-icons/react";
import type { ContextualAction } from "scantling-common";
import {
  getContextualActionsForItem,
  getContextualActionsForPost,
  getContextualActionsForIntegration,
} from "scantling-common";
import { cmdkRegistry, type CmdkGroup } from "../cmdk_registry.js";
import { createCmdkItem } from "../cmdk_item.js";
import { createActionCmdkItem } from "../action_cmdk_item.js";
import {
  useApi,
  useListItemVersions,
  useListPosts,
  useListIntegrations,
  useInvalidateLists,
} from "../components.js";

function getActionIcon(action: ContextualAction) {
  if (
    action.verb === "deleteItem" ||
    action.verb === "deletePost" ||
    action.verb === "deleteIntegration"
  ) {
    return <Trash size={14} />;
  }
  if (action.verb === "moveItem" || action.verb === "movePost") {
    if (action.label.includes("inbox") || action.label.includes("scheduling")) {
      return <ArrowLeft size={14} />;
    }
    return <ArrowRight size={14} />;
  }
  if (action.verb === "createPost") {
    return <PaperPlaneTilt size={14} />;
  }
  if (action.verb === "enableIntegration") {
    return <Power size={14} />;
  }
  if (action.verb === "disableIntegration") {
    return <Prohibit size={14} />;
  }
  return null;
}

interface ParsedRoute {
  type: "item" | "post" | "integration" | null;
  slug: string | null;
}

function parseRoute(path: string): ParsedRoute {
  // Match /o/:orgSlug/i/:itemSlug
  const itemMatch = path.match(/^\/o\/[^/]+\/i\/([^/]+)/);
  if (itemMatch) {
    return { type: "item", slug: itemMatch[1] };
  }

  // Match /o/:orgSlug/p/:postSlug
  const postMatch = path.match(/^\/o\/[^/]+\/p\/([^/]+)/);
  if (postMatch) {
    return { type: "post", slug: postMatch[1] };
  }

  // Match /o/:orgSlug/n/:integrationSlug
  const integrationMatch = path.match(/^\/o\/[^/]+\/n\/([^/]+)/);
  if (integrationMatch) {
    return { type: "integration", slug: integrationMatch[1] };
  }

  return { type: null, slug: null };
}

function useContextualCmdkItems(): CmdkGroup[] {
  const [location] = useLocation();
  const { getToken } = useAuth();
  const pool = useApi();
  const invalidate = useInvalidateLists();

  const { type, slug } = parseRoute(location);

  // Fetch data based on route type
  const itemsResult = useListItemVersions(
    type === "item" && slug ? `slug:${slug}` : undefined,
  );
  const postsResult = useListPosts(
    type === "post" && slug ? `slug:${slug}` : undefined,
  );
  const integrationsResult = useListIntegrations(
    type === "integration" && slug ? `slug:${slug}` : undefined,
  );

  const executeAction = useCallback(
    async (verb: string, params: Record<string, unknown>) => {
      const api = pool.acquire();
      try {
        const token = await getToken();
        if (!token) return;
        const authedApi = await api.authenticate(token);
        const orgApi = authedApi.org;
        if (!orgApi) return;
        await orgApi.executeAction({ verb, params });
        invalidate("listItemVersions");
        invalidate("listPosts");
        invalidate("listIntegrations");
      } finally {
        pool.release();
      }
    },
    [pool, getToken, invalidate],
  );

  return useMemo(() => {
    let actions: ContextualAction[] = [];
    let heading = "";

    if (type === "item" && itemsResult.data?.entries.length) {
      const item = itemsResult.data.entries.reduce((a, b) =>
        a.version > b.version ? a : b,
      );
      actions = getContextualActionsForItem(item);
      heading = `Item: ${item.niceName ?? item.slug}`;
    } else if (type === "post" && postsResult.data?.entries.length) {
      const post = postsResult.data.entries[0];
      actions = getContextualActionsForPost(post);
      heading = `Post: ${post.slug}`;
    } else if (
      type === "integration" &&
      integrationsResult.data?.entries.length
    ) {
      const integration = integrationsResult.data.entries[0];
      actions = getContextualActionsForIntegration(integration);
      heading = `Integration: ${integration.niceName ?? integration.slug}`;
    }

    if (actions.length === 0) {
      return [];
    }

    const items = actions.map(action => {
      if (!action.argument) {
        // Simple action - use base CmdkItem
        return createCmdkItem({
          value: `${action.verb}-${JSON.stringify(action.params)}`,
          label: action.label,
          icon: getActionIcon(action),
          variant: action.variant,
          onSelect: () => executeAction(action.verb, action.params),
        });
      } else {
        // Action with argument - use ActionCmdkItem which fetches its own data
        return createActionCmdkItem(action, executeAction, getActionIcon);
      }
    });

    return [{ heading, items }];
  }, [
    type,
    itemsResult.data,
    postsResult.data,
    integrationsResult.data,
    executeAction,
  ]);
}

cmdkRegistry.register({
  type: "contextual",
  useItems: useContextualCmdkItems,
});
